import React from 'react';
import { CheckIcon, SquareIcon } from 'lucide-react';
import { assemblySteps, cutList, finalChecks, finishingTips, markingTips, projectMeasures } from '../data/content';
import { Eyebrow, SectionLead, SectionTitle } from './ui/Section';
function PreviewCard({
  title,
  children,
  className = ''
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return <article className={`flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`} data-id="element-173" data-mp="3eza51p9atyuh">
      <h3 className="text-sm font-black uppercase tracking-wide text-slate-900 md:text-base" data-id="element-174" data-mp="3a0p3nmib994g">{title}</h3>
      <div className="mt-4 flex-1" data-id="element-175" data-mp="357w2t9pyqkxn">{children}</div>
    </article>;
}
function TipList({
  items
}: {
  items: string[];
}) {
  return <ul className="space-y-1.5 text-xs font-semibold text-slate-600 md:text-sm" data-id="element-176" data-mp="307rui6z7kwii">
      {items.map(item => <li key={item} className="flex gap-2" data-id="element-177" data-mp="1lp0irhg1lei">
          <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" data-id="element-178" data-mp="3sxgv4j80aujl" />
          {item}
        </li>)}
    </ul>;
}
export function InsidePreview() {
  return <section className="border-y border-slate-200 bg-sky-50 px-4 py-12 md:py-16" data-id="element-179" data-mp="3qocyjtuwbh7v">
      <div className="mx-auto max-w-6xl" data-id="element-180" data-mp="3qqfqwkjitmfr">
        <Eyebrow data-id="element-181" data-mp="3byoysx2d4qm9">🔍 Vista por dentro</Eyebrow>
        <SectionTitle data-id="element-182" data-mp="3bxvzgh5qh3e5">
          ¿Qué vas a encontrar en <span className="text-sky-600" data-id="element-183" data-mp="3gy1mtl4z03qf">cada proyecto?</span>
        </SectionTitle>
        <SectionLead data-id="element-184" data-mp="32yimheiehwoo">
          Cada construcción está organizada para que puedas entender qué hacer desde que preparás
          las piezas hasta que terminás el proyecto.
        </SectionLead>

        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-185" data-mp="2t1dzjcgqdx6t">
          <PreviewCard title="📐 Medidas del proyecto" data-id="element-186" data-mp="2t378wziqavii">
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500" data-id="element-187" data-mp="2y0qkfro1ku6r">Ejemplo:</p>
            <dl className="mt-2 space-y-2 text-sm font-semibold text-slate-600" data-id="element-188" data-mp="rasr00g6pr23">
              {projectMeasures.map(([label, value]) => <div key={label} className="flex items-center justify-between gap-2 border-b border-slate-200 pb-2 last:border-b-0" data-id="element-189" data-mp="w683anyjnewe">
                  <dt data-id="element-190" data-mp="puf0xdfiuu0q">{label}</dt>
                  <dd className="font-black text-slate-900" data-id="element-191" data-mp="kszh1op2ytjx">{value}</dd>
                </div>)}
            </dl>
            <p className="mt-3 text-xs font-semibold leading-snug text-slate-500 md:text-sm" data-id="element-192" data-mp="fv7lmkp88paz">
              Cuando corresponde, también vas a encontrar medidas interiores.
            </p>
          </PreviewCard>

          <PreviewCard title="🪵 Lista de cortes" className="lg:col-span-2" data-id="element-193" data-mp="18k4vq0jabfsn">
            <div className="overflow-x-auto" data-id="element-194" data-mp="13rbnh2olxpbt">
              <table className="w-full min-w-[420px] text-left text-xs font-semibold text-slate-600 md:text-sm" data-id="element-195" data-mp="13q0iizjfebj4">
                <thead data-id="element-196" data-mp="1w8e9cf6cgx8p">
                  <tr className="border-b border-slate-200 text-[10px] uppercase tracking-wide text-slate-500" data-id="element-197" data-mp="1w8xus5v7e3rh">
                    <th scope="col" className="pb-1.5" data-id="element-198" data-mp="1rgmswxgwk7wa">Pieza</th>
                    <th scope="col" className="pb-1.5" data-id="element-199" data-mp="1zqbsc0o5k6oi">Función</th>
                    <th scope="col" className="pb-1.5 text-right" data-id="element-200" data-mp="1zs4x1af7ggtv">Medida</th>
                    <th scope="col" className="pb-1.5 text-right" data-id="element-201" data-mp="23sou6dzs3kg0">Cantidad</th>
                  </tr>
                </thead>
                <tbody data-id="element-202" data-mp="28pp1stca0v98">
                  {cutList.map(([piece, role, size, qty]) => <tr key={piece} className="border-b border-slate-200 last:border-b-0" data-id="element-203" data-mp="2dro37xoghi3j">
                      <td className="py-1.5" data-id="element-204" data-mp="2iood6joff4zx">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-sky-600 text-xs font-black text-white" data-id="element-205" data-mp="2mqrmp2rjbzik">
                          {piece}
                        </span>
                      </td>
                      <td className="py-1.5 font-bold text-slate-900" data-id="element-206" data-mp="2wfjenawlijth">{role}</td>
                      <td className="py-1.5 text-right tabular-nums" data-id="element-207" data-mp="31g7caffbxywo">{size}</td>
                      <td className="py-1.5 text-right font-black text-slate-900" data-id="element-208" data-mp="36es5huw5zibm">{qty}</td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm font-black uppercase text-sky-700" data-id="element-209" data-mp="21oej2da3c5v">
              Sabés qué piezas necesitás antes de empezar.
            </p>
          </PreviewCard>

          <PreviewCard title="✏️ Marcado y preparación" data-id="element-210" data-mp="gsnl67ujzqmt">
            <p className="mb-2 text-xs font-semibold text-slate-500 md:text-sm" data-id="element-211" data-mp="br822lbxru72">
              Vas a encontrar indicaciones para:
            </p>
            <TipList items={markingTips} data-id="element-212" data-mp="3k9dxt5kn51xh" />
          </PreviewCard>

          <PreviewCard title="🔩 Armado paso a paso" data-id="element-213" data-mp="3f9i1lsi6qesh">
            <ol className="space-y-1.5 text-xs font-semibold text-slate-600 md:text-sm" data-id="element-214" data-mp="3t7pv1qg0ixyw">
              {assemblySteps.map((step, index) => <li key={step} className="flex items-center gap-2.5" data-id="element-215" data-mp="3p5l4x1orwjh0" data-mpi={index}>
                  <span className="w-14 shrink-0 text-[10px] font-black uppercase tracking-wide text-sky-700" data-id="element-216" data-mp="18h7ggwerpmxk">
                    Paso {index + 1}
                  </span>
                  {step}
                </li>)}
            </ol>
          </PreviewCard>

          <PreviewCard title="🪵 Lijado y terminación" data-id="element-217" data-mp="1gyrcaq8hsaln">
            <p className="mb-2 text-xs font-semibold text-slate-500 md:text-sm" data-id="element-218" data-mp="1h0c80j6jlzl3">
              También vas a encontrar sugerencias para:
            </p>
            <TipList items={finishingTips} data-id="element-219" data-mp="170bj8hpwmr9i" />
          </PreviewCard>

          <PreviewCard title="✅ Control final" className="md:col-span-2 lg:col-span-3" data-id="element-220" data-mp="172o1kvzkr3n3">
            <p className="text-xs font-semibold text-slate-500 md:text-sm" data-id="element-221" data-mp="1uqxroa3tmdk1">
              Antes de utilizar cada proyecto vas a poder comprobar:
            </p>
            <ul className="mt-3 flex flex-wrap gap-2" data-id="element-222" data-mp="1l14h9cff04hd">
              {finalChecks.map(check => <li key={check} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-bold text-slate-800" data-id="element-223" data-mp="1pynyml7esxdj">
                  <SquareIcon className="h-3.5 w-3.5 text-slate-500" aria-hidden="true" data-id="element-224" data-mp="2igicyzng5mwh" />
                  {check}
                </li>)}
            </ul>
          </PreviewCard>
        </div>
      </div>
    </section>;
}